import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart, Line, Cell, PieChart, Pie, AreaChart, Area
} from 'recharts';
import {
  BarChart3, Package, Truck, Users, Building2, TrendingUp,
  FileText, Download, Upload, Database, ShoppingBag, Activity,
  DollarSign, Gauge, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Paleta de colores ──────────────────────────────────────────────
const C_DARK   = '#3d5a30';   // verde oscuro (barras principales)
const C_LIME   = '#c5d86d';   // amarillo-verde (compromisos target)
const C_YELLOW = '#d4ac0d';   // dorado (línea camiones)
const C_LIGHT  = '#7aab5e';   // verde claro (segunda barra agrupada)

const fmtTons = (v) => v == null ? '-' : `${Number(v).toLocaleString('es-CL', { maximumFractionDigits: 0 })} T`;
const fmtUSD  = (v) => v == null ? '-' : `$${Number(v).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

// ── Encabezado de sección ─────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 px-5 py-3 rounded-sm mb-5"
    style={{ background: '#3d5a30' }}>
    <div className="p-1.5 rounded" style={{ background: C_LIME }}>
      <Icon size={16} style={{ color: '#3d5a30' }} />
    </div>
    <span className="text-white font-bold text-sm tracking-wide uppercase">{title}</span>
  </div>
);

// ── Tarjeta de panel ──────────────────────────────────────────────
const Panel = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white border border-slate-200 rounded-sm shadow-sm p-4 ${className}`}>
    <p className="text-sm font-semibold text-slate-700 leading-tight">{title}</p>
    {subtitle && <p className="text-xs text-slate-400 mt-0.5 mb-3">{subtitle}</p>}
    {!subtitle && <div className="mb-3" />}
    {children}
  </div>
);

// ── Tooltip personalizado ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <span className="font-mono font-bold">{typeof p.value === 'number' ? p.value.toLocaleString('es-CL', { maximumFractionDigits: 2 }) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

// ── Tick Y-axis: siempre en una sola línea (trunca si es muy largo) ─
const SingleLineTick = ({ x, y, payload }) => {
  const MAX_LEN = 14;
  const txt = payload.value.length > MAX_LEN
    ? payload.value.substring(0, MAX_LEN - 1) + '…'
    : payload.value;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill="#555" fontSize={9}>
      {txt}
    </text>
  );
};

// ── Tooltip personalizado con tons + USD ──────────────────────────
const VolClienteTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-3 text-xs min-w-[130px]">
      <p className="font-bold text-slate-800 mb-2">{label}</p>
      <p className="text-slate-600">Tons: <span className="font-mono font-semibold">{Number(d?.tons ?? 0).toLocaleString('es-CL', { maximumFractionDigits: 1 })}</span></p>
      <p className="text-emerald-700">USD: <span className="font-mono font-semibold">{fmtUSD(d?.usd)}</span></p>
    </div>
  );
};

// ── Panel 1: Volumen por cliente ──────────────────────────────────
const PanelVolumenCliente = ({ data }) => {
  // Gráfico: top 5 por tons
  const chartData = data.slice(0, 5);
  const max = Math.max(...chartData.map(d => d.tons), 1);
  const barColors = ['#3d5a30', '#4a6f3c', '#5a8049', '#6a9356', '#7aab5e'];

  return (
    <>
      {/* Gráfico top 5 */}
      <ResponsiveContainer width="100%" height={195}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 68, right: 16, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
          <XAxis
            type="number"
            domain={[0, max * 1.1]}
            tick={{ fontSize: 9 }}
            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
          />
          <YAxis
            type="category"
            dataKey="cliente"
            tick={<SingleLineTick />}
            width={66}
          />
          <Tooltip content={<VolClienteTooltip />} />
          <Bar dataKey="tons" name="Tons" radius={[0, 3, 3, 0]}>
            {chartData.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Tabla: header fijo + scroll para ver todos los clientes */}
      <div className="mt-3 border border-slate-200 rounded overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: '162px' }}>
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 border-b border-slate-200">
                <th className="px-2 py-1.5 text-left font-semibold text-slate-600">CLIENTE</th>
                <th className="px-2 py-1.5 text-right font-semibold text-slate-600">TONELADAS</th>
                <th className="px-2 py-1.5 text-right font-semibold text-slate-600">USD</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-2 py-1 font-medium text-slate-700">{c.cliente}</td>
                  <td className="px-2 py-1 text-right font-mono font-semibold text-slate-700">
                    {Number(c.tons).toLocaleString('es-CL', { maximumFractionDigits: 1 })} T
                  </td>
                  <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700">
                    {fmtUSD(c.usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ── Tooltip Compromisos ───────────────────────────────────────────
const CompromisosTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const entregadas = payload.find(p => p.dataKey === 'entregadas')?.value ?? 0;
  const pendiente  = payload.find(p => p.dataKey === 'pendiente')?.value  ?? 0;
  const pactadas   = entregadas + pendiente;
  const fmtT = v => `${Number(v).toLocaleString('es-CL', { maximumFractionDigits: 2 })} T`;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-3 text-xs min-w-[160px]">
      <p className="font-bold text-slate-800 mb-2">{label}</p>
      <p className="text-slate-500">Total negocio: <span className="font-mono font-semibold text-slate-700">{fmtT(pactadas)}</span></p>
      <p style={{ color: C_DARK }}>Entregado: <span className="font-mono font-semibold">{fmtT(entregadas)}</span></p>
      <p style={{ color: '#8a9e30' }}>Pendiente: <span className="font-mono font-semibold">{fmtT(pendiente)}</span></p>
    </div>
  );
};

// ── Panel 2: Compromisos ──────────────────────────────────────────
const PanelCompromisos = ({ data }) => {
  const chartData = data.map(d => ({
    ...d,
    pendiente: Math.max(0, d.pactadas - d.entregadas),
  }));
  const chartH = Math.max(160, chartData.length * 36 + 20);

  return (
    <ResponsiveContainer width="100%" height={chartH}>
      <BarChart
        data={chartData}
        layout="vertical"
        barSize={14}
        barCategoryGap="35%"
        margin={{ left: 68, right: 12, top: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
        <XAxis type="number" tickFormatter={v => `${v}T`} tick={{ fontSize: 9 }} />
        <YAxis type="category" dataKey="cliente" tick={<SingleLineTick />} width={66} />
        <Tooltip content={<CompromisosTooltip />} />
        <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
        <Bar dataKey="entregadas" name="Entregado"     fill={C_DARK} stackId="prog" radius={[0, 0, 0, 0]} />
        <Bar dataKey="pendiente"  name="Pendiente"     fill={C_LIME} stackId="prog" radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Tooltip Inventario ────────────────────────────────────────────
const InventarioTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const fmtT = v => `${Number(v ?? 0).toLocaleString('es-CL', { maximumFractionDigits: 2 })} T`;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-3 text-xs min-w-[165px]">
      <p className="font-bold text-slate-800 mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.fill }}>{p.name}: <span className="font-mono font-semibold">{fmtT(p.value)}</span></p>
      ))}
    </div>
  );
};

// ── Panel 3: Inventario ───────────────────────────────────────────
const PanelInventario = ({ data }) => (
  <ResponsiveContainer width="100%" height={220}>
    <BarChart data={data} margin={{ left: 10, right: 10, top: 0, bottom: 0 }} barCategoryGap="40%">
      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
      <XAxis dataKey="grano" tick={{ fontSize: 11 }} />
      <YAxis tick={{ fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
      <Tooltip content={<InventarioTooltip />} />
      <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
      <Bar dataKey="pend_entrega" name="Pend. de entrega" fill={C_LIGHT} stackId="inv" radius={[0, 0, 0, 0]} />
      <Bar dataKey="pend_venta"   name="Pend. de venta"   fill={C_DARK}  stackId="inv" radius={[3, 3, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

// ── Tooltip Volumen Mes ───────────────────────────────────────────
const VolumenMesTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const tons     = payload.find(p => p.dataKey === 'tons')?.value     ?? 0;
  const camiones = payload.find(p => p.dataKey === 'camiones')?.value ?? 0;
  const fmtT = v => Number(v).toLocaleString('es-CL', { maximumFractionDigits: 2 });
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-3 text-xs min-w-[165px]">
      <p className="font-bold text-slate-800 mb-2">{label}</p>
      <p className="text-slate-500">Tons despachadas: <span className="font-mono font-semibold text-slate-700">{fmtT(tons)}</span></p>
      <p style={{ color: C_YELLOW }}># Camiones: <span className="font-mono font-semibold">{camiones}</span></p>
    </div>
  );
};

// ── Panel 4: Volumen despachado por mes ───────────────────────────
const PanelVolumenMes = ({ data }) => (
  <ResponsiveContainer width="100%" height={240}>
    <ComposedChart data={data} margin={{ left: 10, right: 30, top: 5, bottom: 20 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
      <XAxis dataKey="mes" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={45} />
      <YAxis yAxisId="tons" orientation="left"  tick={{ fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} label={{ value: 'Tons', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#999' } }} />
      <YAxis yAxisId="cam"  orientation="right" tick={{ fontSize: 9 }} label={{ value: '# Cam.', angle: 90, position: 'insideRight', style: { fontSize: 9, fill: '#999' } }} />
      <Tooltip content={<VolumenMesTooltip />} />
      <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
      <Bar  yAxisId="tons" dataKey="tons"     name="Tons despachadas" fill={C_DARK}   radius={[3,3,0,0]} />
      <Line yAxisId="cam"  dataKey="camiones" name="# Camiones"       stroke={C_YELLOW} strokeWidth={2} dot={{ r: 3, fill: C_YELLOW }} type="monotone" />
    </ComposedChart>
  </ResponsiveContainer>
);

// ── Tooltip Proyección de Carga ───────────────────────────────────
const ProyeccionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const detalles = payload[0]?.payload?.detalles ?? [];
  const totalCam = payload[0]?.payload?.camiones ?? 0;
  const totalTons = payload[0]?.payload?.tons ?? 0;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-3 text-xs min-w-[190px]">
      <p className="font-bold text-slate-800 mb-2">Fecha: {label}</p>
      {detalles.map((d, i) => (
        <div key={i} className="flex justify-between gap-3 py-0.5 border-b border-slate-100 last:border-0">
          <span className="font-medium text-slate-600 truncate">Despacho {d.despacho}</span>
          <span style={{ color: C_DARK }} className="font-mono font-semibold whitespace-nowrap">
            {d.camiones} cam · {d.tons} T
          </span>
        </div>
      ))}
      <div className="mt-2 pt-1 border-t border-slate-200 flex justify-between font-semibold">
        <span className="text-slate-700">Total</span>
        <span style={{ color: C_DARK }} className="font-mono">{totalCam} cam · {totalTons} T</span>
      </div>
    </div>
  );
};

// ── Panel Proyección de Carga ─────────────────────────────────────
const PanelProyeccion = ({ data }) => {
  if (!data?.length) return (
    <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Sin datos futuros</div>
  );

  // Aplanar todos los detalles de todas las fechas para la tabla
  const allRows = data.flatMap(d => d.detalles || []);

  const COLS = [
    { key: 'fecha_carga',    label: 'F. Carga' },
    { key: 'fecha_descarga', label: 'F. Descarga' },
    { key: 'despacho',       label: 'N° Despacho' },
    { key: 'camiones',       label: 'Camiones' },
    { key: 'tons',           label: 'Tons' },
    { key: 'corredor',       label: 'Corredor' },
    { key: 'destino',        label: 'Destino' },
  ];

  return (
    <div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ left: 10, right: 10, top: 5, bottom: 20 }} barCategoryGap="40%">
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={45} />
          <YAxis tick={{ fontSize: 9 }} label={{ value: '# Cam.', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#999' } }} allowDecimals={false} />
          <Tooltip content={<ProyeccionTooltip />} />
          <Bar dataKey="camiones" name="Camiones" fill={C_DARK} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {/* Tabla detalle */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {COLS.map(c => (
                <th key={c.key} className="px-2 py-1.5 text-center font-semibold text-slate-600 whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allRows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                {COLS.map(c => (
                  <td key={c.key} className="px-2 py-1.5 text-center text-slate-700 whitespace-nowrap">
                    {c.key === 'tons'
                      ? row[c.key] != null ? `${Number(row[c.key]).toLocaleString('es-CL')} T` : ''
                      : row[c.key] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
const PanelRanking = ({ data }) => {
  const barColors = [C_DARK, '#4a6f3c', '#5a8049', '#6a9356', '#7aab5e', '#8dbf72', '#a0cc85', '#b3d998'];
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} layout="vertical" margin={{ left: 70, right: 30, top: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
        <XAxis type="number" tick={{ fontSize: 9 }} />
        <YAxis type="category" dataKey="empresa" tick={<SingleLineTick />} width={68} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="camiones" name="Camiones" radius={[0,3,3,0]}>
          {data.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Panel 6: Tabla de pagos ───────────────────────────────────────
const ESTADOS_PAGO = ['Todos', 'Pendiente', 'Emitido', 'Pagado'];
const ESTADO_STYLES = {
  Pendiente: 'bg-yellow-100 text-yellow-800',
  Emitido:   'bg-blue-100 text-blue-800',
  Pagado:    'bg-green-100 text-green-800',
};

const PanelPagos = ({ data }) => {
  const [filtroEstado, setFiltroEstado] = useState('Emitido');
  const filtrado = filtroEstado === 'Todos' ? data : data.filter(p => p.estado === filtroEstado);
  const total = filtrado.reduce((acc, p) => acc + (p.monto || 0), 0);

  return (
    <div>
      {/* Filtro de estado */}
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {ESTADOS_PAGO.map(e => (
          <button key={e} onClick={() => setFiltroEstado(e)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
              filtroEstado === e
                ? 'border-transparent text-white'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
            }`}
            style={filtroEstado === e ? { background: C_DARK } : {}}
          >{e}</button>
        ))}
      </div>
      <div className="overflow-auto max-h-52">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
              {['N° DESPACHO','TRANSPORTISTA','FACTURA','N° CHEQUE','ESTADO','MONTO'].map(h => (
                <th key={h} className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtrado.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-400">Sin registros</td></tr>
            )}
            {filtrado.map((p, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1.5 font-mono">{p.dispatch_number || '-'}</td>
                <td className="px-2 py-1.5">{p.transportista || '-'}</td>
                <td className="px-2 py-1.5 text-center">{p.factura || '-'}</td>
                <td className="px-2 py-1.5 text-center">{p.numero_cheque || '-'}</td>
                <td className="px-2 py-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ESTADO_STYLES[p.estado] || 'bg-slate-100 text-slate-600'}`}>
                    {p.estado}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono text-right">{fmtUSD(p.monto)}</td>
              </tr>
            ))}
          </tbody>
          {filtrado.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 font-semibold border-t border-slate-300">
                <td colSpan={5} className="px-2 py-1.5 text-right text-slate-600">TOTAL</td>
                <td className="px-2 py-1.5 font-mono text-right" style={{ color: C_DARK }}>{fmtUSD(total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ── Colores para el pie chart de Cuentas por Cobrar ─────────────────
const PIE_COLORS = ['#3d5a30', '#4a6f3c', '#5a8049', '#6a9356', '#7aab5e'];

// ── Tooltip del Pie Chart ───────────────────────────────────────────
const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md p-3 text-xs min-w-[140px]">
      <p className="font-bold text-slate-800 mb-2">{d?.cliente}</p>
      <p className="text-emerald-700">Deuda USD: <span className="font-mono font-semibold">{fmtUSD(d?.deuda_usd)}</span></p>
      <p className="text-slate-600">Facturas: <span className="font-mono font-semibold">{d?.cantidad_facturas}</span></p>
    </div>
  );
};

// ── Panel: Cuentas por Cobrar (Pie + Tabla) ─────────────────────────
const PanelCuentasPorCobrar = ({ data }) => {
  // Agrupar por cliente para el pie chart (top 5)
  const clienteMap = {};
  data.forEach(c => {
    const clave = c.cliente_alias || 'Sin Cliente';
    if (!clienteMap[clave]) {
      clienteMap[clave] = { cliente: clave, deuda_usd: 0, cantidad_facturas: 0 };
    }
    clienteMap[clave].deuda_usd += c.monto_usd_neto || 0;
    clienteMap[clave].cantidad_facturas += 1;
  });
  
  const clienteData = Object.values(clienteMap)
    .sort((a, b) => b.deuda_usd - a.deuda_usd);
  
  const top5 = clienteData.slice(0, 5);
  const totalDeuda = clienteData.reduce((acc, c) => acc + c.deuda_usd, 0);

  // Tabla detallada por factura (solo pendientes/vencidas)
  const tablaData = data
    .filter(c => c.estado !== 'pagada')
    .sort((a, b) => (b.monto_usd_neto || 0) - (a.monto_usd_neto || 0));

  return (
    <div className="flex gap-6">
      {/* Pie Chart - Top 5 con leyenda */}
      <div className="w-2/5">
        <p className="text-[10px] text-slate-500 uppercase font-semibold mb-2">Top 5 Mayores Deudas</p>
        <div className="flex items-center gap-2">
          <ResponsiveContainer width="60%" height={180}>
            <PieChart>
              <Pie
                data={top5}
                dataKey="deuda_usd"
                nameKey="cliente"
                cx="50%"
                cy="50%"
                outerRadius={65}
                innerRadius={30}
                paddingAngle={2}
              >
                {top5.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {/* Leyenda */}
          <div className="flex-1 space-y-1.5">
            {top5.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px]">
                <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i] }} />
                <span className="truncate max-w-[80px] text-slate-700" title={c.cliente}>{c.cliente}</span>
                <span className="font-mono font-semibold text-emerald-700 ml-auto">{fmtUSD(c.deuda_usd)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-center mt-2 pt-2 border-t border-slate-200">
          <span className="text-xs text-slate-500">Total deuda: </span>
          <span className="text-sm font-mono font-bold text-emerald-700">{fmtUSD(totalDeuda)}</span>
        </div>
      </div>
      
      {/* Tabla detallada */}
      <div className="w-3/5">
        <p className="text-[10px] text-slate-500 uppercase font-semibold mb-2">Detalle por Factura (Pendientes)</p>
        <div className="overflow-auto max-h-56 border border-slate-200 rounded">
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 border-b border-slate-200">
                <th className="px-2 py-1.5 text-left font-semibold text-slate-600">CLIENTE</th>
                <th className="px-2 py-1.5 text-left font-semibold text-slate-600">CORREDOR</th>
                <th className="px-2 py-1.5 text-left font-semibold text-slate-600">N° DESPACHO</th>
                <th className="px-2 py-1.5 text-left font-semibold text-slate-600">N° FACTURA</th>
                <th className="px-2 py-1.5 text-right font-semibold text-slate-600">USD</th>
                <th className="px-2 py-1.5 text-center font-semibold text-slate-600">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {tablaData.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-slate-400">Sin deudas pendientes</td></tr>
              )}
              {tablaData.map((c, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-2 py-1 font-medium text-slate-700 truncate max-w-[90px]" title={c.cliente_alias}>
                    {c.cliente_alias || 'N/A'}
                  </td>
                  <td className="px-2 py-1 text-slate-600 truncate max-w-[70px]" title={c.broker}>
                    {c.broker || '—'}
                  </td>
                  <td className="px-2 py-1 font-mono text-blue-600">{c.dispatch_number || '—'}</td>
                  <td className="px-2 py-1 font-mono text-slate-600">{c.factura || '—'}</td>
                  <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700">
                    {fmtUSD(c.monto_usd_neto)}
                  </td>
                  <td className="px-2 py-1 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                      c.estado === 'vencida' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'
                    }`}>
                      {c.estado === 'vencida' ? 'VENCIDA' : 'PEND.'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            {tablaData.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 font-semibold border-t border-slate-300">
                  <td colSpan={3} className="px-2 py-1.5 text-right text-slate-600">TOTAL PENDIENTE</td>
                  <td className="px-2 py-1.5 font-mono text-right text-emerald-700">
                    {fmtUSD(tablaData.reduce((acc, c) => acc + (c.monto_usd_neto || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Panel: Indicadores Financieros ──────────────────────────────────
const PanelFinanciero = ({ data }) => {
  if (!data) return null;
  
  const {
    facturacion_total = 0,
    toneladas_totales = 0,
    margen_bruto_pct = 0,
    margen_por_ton = 0,
    utilidad_mensual = []
  } = data;
  
  const META_MARGEN_TON = 15; // USD por tonelada
  const porcentajeMeta = Math.min((margen_por_ton / META_MARGEN_TON) * 100, 100);
  
  // Colores del velocímetro según rendimiento
  const getGaugeColor = (pct) => {
    if (pct >= 80) return '#22c55e'; // verde
    if (pct >= 50) return '#eab308'; // amarillo
    return '#ef4444'; // rojo
  };
  
  // Formatear número grande
  const fmtLarge = (n) => {
    if (n >= 1000000) return `${(n/1000000).toFixed(2)}M`;
    if (n >= 1000) return `${(n/1000).toFixed(1)}K`;
    return n.toFixed(0);
  };

  return (
    <div className="space-y-4">
      {/* Fila de tarjetas */}
      <div className="grid grid-cols-4 gap-3">
        {/* Facturación Total */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={16} className="text-emerald-600" />
            <span className="text-[10px] font-semibold text-emerald-700 uppercase">Facturación Total</span>
          </div>
          <p className="text-2xl font-bold text-emerald-800">USD {fmtLarge(facturacion_total)}</p>
          <p className="text-xs text-emerald-600 mt-1">{fmtTons(toneladas_totales)} vendidas</p>
        </div>
        
        {/* Margen Bruto % */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={16} className="text-blue-600" />
            <span className="text-[10px] font-semibold text-blue-700 uppercase">Margen Bruto</span>
          </div>
          <p className="text-2xl font-bold text-blue-800">{margen_bruto_pct.toFixed(1)}%</p>
          <p className="text-xs text-blue-600 mt-1">Sobre ventas totales</p>
        </div>
        
        {/* Margen por Tonelada (Velocímetro) */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Gauge size={16} className="text-slate-600" />
            <span className="text-[10px] font-semibold text-slate-700 uppercase">Margen x Ton</span>
          </div>
          <div className="relative flex items-center justify-center">
            {/* Velocímetro SVG */}
            <svg width="100" height="60" viewBox="0 0 100 60">
              {/* Fondo del arco */}
              <path
                d="M 10 50 A 40 40 0 0 1 90 50"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="8"
                strokeLinecap="round"
              />
              {/* Arco de progreso */}
              <path
                d="M 10 50 A 40 40 0 0 1 90 50"
                fill="none"
                stroke={getGaugeColor(porcentajeMeta)}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${porcentajeMeta * 1.26} 126`}
              />
              {/* Texto central */}
              <text x="50" y="45" textAnchor="middle" className="text-lg font-bold" fill="#1e293b">
                ${margen_por_ton.toFixed(1)}
              </text>
              <text x="50" y="56" textAnchor="middle" className="text-[8px]" fill="#64748b">
                Meta: ${META_MARGEN_TON}
              </text>
            </svg>
          </div>
        </div>
        
        {/* Target */}
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Target size={16} className="text-violet-600" />
            <span className="text-[10px] font-semibold text-violet-700 uppercase">Cumplimiento Meta</span>
          </div>
          <p className="text-2xl font-bold text-violet-800">{porcentajeMeta.toFixed(0)}%</p>
          <div className="w-full bg-violet-200 rounded-full h-2 mt-2">
            <div 
              className="h-2 rounded-full transition-all duration-500"
              style={{ 
                width: `${Math.min(porcentajeMeta, 100)}%`,
                backgroundColor: getGaugeColor(porcentajeMeta)
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Gráfico de Utilidad Mensual */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <p className="text-[10px] text-slate-500 uppercase font-semibold mb-3">Utilidad Mensual (Últimos 12 Meses)</p>
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={utilidad_mensual} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorUtilidad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis 
              dataKey="mes" 
              tick={{ fontSize: 9, fill: '#64748b' }} 
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 9, fill: '#64748b' }} 
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${fmtLarge(v)}`}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '11px'
              }}
              formatter={(value) => [`$${fmtLarge(value)}`, 'Utilidad']}
            />
            <Area 
              type="monotone" 
              dataKey="utilidad" 
              stroke="#22c55e" 
              strokeWidth={2}
              fill="url(#colorUtilidad)"
              dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: '#16a34a' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [indicadores, setIndicadores] = useState(null);
  const [cuentasPorCobrar, setCuentasPorCobrar] = useState([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [clearExisting, setClearExisting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchStats();
    fetchIndicadores();
    fetchCuentasPorCobrar();
  }, []);

  const fetchIndicadores = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dashboard/indicadores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndicadores(res.data);
    } catch {
      // silencioso
    }
  };

  const fetchCuentasPorCobrar = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/cuentas-por-cobrar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCuentasPorCobrar(res.data);
    } catch {
      // silencioso
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  // Exportar todos los datos
  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/admin/export-data`);
      const data = response.data;
      
      // Crear archivo JSON para descargar
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `cargo_control_backup_${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Datos exportados: ${data.summary.companies} empresas, ${data.summary.trucks} camiones, ${data.summary.dispatches} despachos`);
      setIsExportModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar datos');
    } finally {
      setExportLoading(false);
    }
  };

  // Importar datos desde archivo
  const handleImportData = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImportLoading(true);
    try {
      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);
      
      // Preparar datos para importación
      const importPayload = {
        users: [], // No importamos usuarios por seguridad
        companies: jsonData.companies || [],
        trucks: jsonData.trucks || [],
        trailers: jsonData.trailers || [],
        drivers: jsonData.drivers || [],
        importadoras: jsonData.importadoras || [],
        dispatches: jsonData.dispatches || [],
        admin_assignments: jsonData.admin_assignments || [],
        clear_existing: clearExisting
      };
      
      const response = await axios.post(`${API}/admin/import-data`, importPayload);
      const results = response.data.results;
      
      toast.success(`Importación completada: ${results.imported.companies || 0} empresas, ${results.imported.trucks || 0} camiones, ${results.imported.dispatches || 0} despachos`);
      
      // Refrescar estadísticas
      fetchStats();
      setIsImportModalOpen(false);
      setClearExisting(false);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('El archivo no es un JSON válido');
      } else {
        toast.error(error.response?.data?.detail || 'Error al importar datos');
      }
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const statCards = [
    {
      title: 'Total Despachos',
      value: stats?.total_dispatches || 0,
      icon: FileText,
      color: 'bg-blue-500',
      testId: 'stat-total-dispatches'
    },
    {
      title: 'Pendientes',
      value: stats?.pending_dispatches || 0,
      icon: Package,
      color: 'bg-yellow-500',
      testId: 'stat-pending-dispatches'
    },
    {
      title: 'En Proceso',
      value: stats?.in_progress_dispatches || 0,
      icon: TrendingUp,
      color: 'bg-orange-500',
      testId: 'stat-in-progress-dispatches'
    },
    {
      title: 'Finalizados',
      value: stats?.completed_dispatches || 0,
      icon: BarChart3,
      color: 'bg-green-500',
      testId: 'stat-completed-dispatches'
    },
    {
      title: 'Importadores',
      value: stats?.total_importadoras || 0,
      icon: Building2,
      color: 'bg-purple-500',
      testId: 'stat-total-importadoras'
    },
    {
      title: 'Empresas',
      value: stats?.total_companies || 0,
      icon: Building2,
      color: 'bg-indigo-500',
      testId: 'stat-total-companies'
    },
    {
      title: 'Camiones',
      value: stats?.total_trucks || 0,
      icon: Truck,
      color: 'bg-cyan-500',
      testId: 'stat-total-trucks'
    },
    {
      title: 'Choferes',
      value: stats?.total_drivers || 0,
      icon: Users,
      color: 'bg-pink-500',
      testId: 'stat-total-drivers'
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Dashboard</h1>
            <p className="text-slate-600 mt-1">Bienvenido, {user?.full_name}</p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'MASTER' && (
              <>
                <Button
                  onClick={() => setIsExportModalOpen(true)}
                  variant="outline"
                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  data-testid="export-data-button"
                >
                  <Download size={16} className="mr-1" /> Exportar Datos
                </Button>
                <Button
                  onClick={() => setIsImportModalOpen(true)}
                  variant="outline"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="import-data-button"
                >
                  <Upload size={16} className="mr-1" /> Importar Datos
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.testId}
                data-testid={stat.testId}
                className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">{stat.title}</p>
                    <p className="text-3xl font-heading font-bold text-slate-900 mt-2 font-mono">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-sm`}>
                    <Icon className="text-white" size={24} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
          <h2 className="text-xl font-heading font-semibold text-slate-900 mb-4">Vista General</h2>
          <p className="text-slate-600">
            Sistema de gestión de despachos y transporte. Utiliza el menú lateral para navegar entre las diferentes secciones.
          </p>
        </div>

        {/* ═══ INDICADORES COMERCIALES ═══════════════════════════════ */}
        {indicadores && (
          <div data-testid="indicadores-comerciales">
            <SectionHeader icon={ShoppingBag} title="Indicadores Comerciales" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <Panel
                title="Volumen de ventas por cliente (tons - USD)"
                subtitle="Top 5 por toneladas — tabla completa debajo"
              >
                {indicadores.comerciales.volumen_por_cliente.length ? (
                  <PanelVolumenCliente data={indicadores.comerciales.volumen_por_cliente} />
                ) : <p className="text-slate-400 text-xs text-center py-10">Sin datos</p>}
              </Panel>

              <Panel
                title="Compromisos"
                subtitle="Avance en el compromiso de entrega a cliente medido en toneladas"
              >
                {indicadores.comerciales.compromisos.length ? (
                  <PanelCompromisos data={indicadores.comerciales.compromisos} />
                ) : <p className="text-slate-400 text-xs text-center py-10">Sin datos</p>}
              </Panel>

              <Panel
                title="Inventario"
                subtitle="Estado de las existencias aperturado por producto"
              >
                {indicadores.comerciales.inventario.length ? (
                  <PanelInventario data={indicadores.comerciales.inventario} />
                ) : <p className="text-slate-400 text-xs text-center py-10">Sin datos</p>}
              </Panel>
            </div>
          </div>
        )}

        {/* ═══ INDICADORES OPERACIONALES ═════════════════════════════ */}
        {indicadores && (
          <div data-testid="indicadores-operacionales">
            <SectionHeader icon={Activity} title="Indicadores Operacionales" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <Panel
                title="Volumen despachado"
                subtitle="Volumen despachado por mes medido en cantidad de camiones y toneladas"
              >
                {indicadores.operacionales.volumen_por_mes.length ? (
                  <PanelVolumenMes data={indicadores.operacionales.volumen_por_mes} />
                ) : <p className="text-slate-400 text-xs text-center py-10">Sin datos</p>}
              </Panel>

              <Panel
                title="Proyección de carga"
                subtitle="Estimación de camiones a cargar para el cumplimiento de la meta"
              >
                <PanelProyeccion data={indicadores.operacionales.proyeccion_carga} />
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel
                title="Ranking de transportistas"
                subtitle="Ranking medido en cantidad de camiones despachados"
              >
                {indicadores.operacionales.ranking_transportistas.length ? (
                  <PanelRanking data={indicadores.operacionales.ranking_transportistas} />
                ) : <p className="text-slate-400 text-xs text-center py-10">Sin datos</p>}
              </Panel>

              <Panel
                title="Pago de transporte"
              >
                <PanelPagos data={indicadores.operacionales.pagos} />
              </Panel>
            </div>
          </div>
        )}

        {/* ═══ INDICADORES FINANCIEROS ══════════════════════════════ */}
        {(cuentasPorCobrar.length > 0 || indicadores?.financieros) && (
          <div data-testid="indicadores-financieros">
            <SectionHeader icon={DollarSign} title="Indicadores Financieros" />
            
            {/* Panel de Métricas Financieras */}
            {indicadores?.financieros && (
              <div className="mb-4">
                <Panel
                  title="Resumen Financiero"
                  subtitle="Facturación, márgenes y utilidad mensual"
                >
                  <PanelFinanciero data={indicadores.financieros} />
                </Panel>
              </div>
            )}
            
            {/* Panel de Cuentas por Cobrar */}
            {cuentasPorCobrar.length > 0 && (
              <div className="grid grid-cols-1 gap-4">
                <Panel
                  title="Cuentas por Cobrar"
                  subtitle="Deudas pendientes por cliente — Top 5 mayores deudas y detalle por factura"
                >
                  <PanelCuentasPorCobrar data={cuentasPorCobrar} />
                </Panel>
              </div>
            )}
          </div>
        )}

        {/* Modal de Exportación */}
        <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database size={20} className="text-emerald-600" />
                Exportar Todos los Datos
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                <p className="text-sm text-emerald-700">
                  Se exportarán todos los datos del sistema en formato JSON:
                </p>
                <ul className="text-xs text-emerald-600 mt-2 space-y-1 list-disc list-inside">
                  <li>Empresas, Camiones, Acoplados, Choferes</li>
                  <li>Importadores</li>
                  <li>Despachos (con todos sus camiones)</li>
                  <li>Asignaciones de administradores</li>
                </ul>
              </div>
              <p className="text-xs text-slate-500">
                Nota: Las contraseñas de usuarios NO se exportan por seguridad.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setIsExportModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleExportData}
                  disabled={exportLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {exportLoading ? 'Exportando...' : 'Descargar JSON'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Importación */}
        <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload size={20} className="text-blue-600" />
                Importar Datos
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                <p className="text-sm text-blue-700">
                  Selecciona un archivo JSON exportado previamente para importar los datos.
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={clearExisting}
                    onChange={(e) => setClearExisting(e.target.checked)}
                    className="rounded border-amber-400"
                  />
                  <span className="text-sm text-amber-700">
                    Borrar datos existentes antes de importar
                  </span>
                </label>
                <p className="text-xs text-amber-600 mt-1 ml-6">
                  ⚠️ Esto eliminará TODOS los datos actuales (excepto usuarios)
                </p>
              </div>

              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImportData}
                  className="hidden"
                  id="import-file-input"
                />
                <label 
                  htmlFor="import-file-input"
                  className="flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  {importLoading ? (
                    <span className="text-sm text-slate-600">Importando datos...</span>
                  ) : (
                    <>
                      <Upload size={20} className="text-slate-400" />
                      <span className="text-sm text-slate-600">Click para seleccionar archivo JSON</span>
                    </>
                  )}
                </label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setIsImportModalOpen(false); setClearExisting(false); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Dashboard;